import type { ReactElement } from "react";
import { cn } from "../lib/utils.js";
import type { ProposalApplyOutcome } from "./proposal-apply-outcome.js";

/**
 * PB-4923 AC6 — what a settled proposal actually did, said neutrally.
 *
 * Deliberately NOT the red block its sibling uses, and not the DS `Alert`.
 *
 * The red block is the failure surface: a partial apply is not a failure, and
 * painting "Added 3 causes, skipped 1" in critical tokens would report working
 * conflict-avoidance as something broken — the exact misreport this notice
 * exists to prevent. So `partial` is neutral (the card's own muted tokens) and
 * only the all-skipped case takes `watch`, because nothing landed.
 *
 * `Alert` was the other candidate and is wrong for a different reason: it
 * hardcodes `role="alert"`, an assertive live region. This describes something
 * that has already settled and is rendered on mount, so an assertive
 * announcement would interrupt a reader for old news. `role="status"` is the
 * polite equivalent, and matches the rule the DS applies elsewhere (see
 * `ModuleHomeStaleNotice`: nothing on screen is broken, so it must not
 * interrupt).
 *
 * Typography and spacing are the card's existing scale (`text-dense-1`,
 * `leading-snug`, `mt-2`), so it sits with the blocks around it rather than
 * introducing a shape the rail does not already use.
 */
export function ProposalOutcomeNotice({
  outcome,
}: {
  readonly outcome: ProposalApplyOutcome;
}): ReactElement {
  const allSkipped = outcome.status === "skipped";
  return (
    <div
      role="status"
      data-testid="proposal-outcome-notice"
      className={cn(
        "mt-2 rounded border px-2 py-1.5 text-dense-1 leading-snug",
        allSkipped
          ? "border-pb-amber/30 bg-pb-amber-soft text-pb-amber-text"
          : "border-pb-border bg-pb-card text-pb-text-secondary",
      )}
    >
      <span className="font-medium text-pb-text">{outcome.headline}</span>
      {/* Every skip gets its own line. A partial apply commonly has one, but
          listing them is the point — "3 skipped" without WHY is the state this
          replaces. */}
      <ul className="mt-1 list-none space-y-0.5">
        {outcome.reasons.map((reason, index) => (
          <li key={`${index}-${reason}`}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}
